import { $modelRelationships, $coreImplementation } from "@gadgetinc/core";
import { pipe, map } from "wonka";
import { GadgetConnection } from "./connection/GadgetConnection.js";
import { InternalModelManager } from "./connection/InternalModelManager.js";
import { AuthenticationMode } from "./connection/ClientOptions.js";
import { assert, GadgetClientError, disambiguateActionVariables, disambiguateBulkActionVariables, capitalizeIdentifier, namespaceDataPath, camelize, isEqual } from "./connection/support.js";
import { ErrorWrapper } from "./connection/ErrorWrapper.js";
import { GadgetRecord } from "./connection/GadgetRecord.js";
import { enqueueActionRunner } from "./connection/operationRunners.js";
import { BackgroundActionHandle } from "./connection/BackgroundActionHandle.js";
;
import { buildGlobalAction, buildComputedView, buildInlineComputedView } from "./builder.js";
import { SessionManager } from "./models/Session.js";
import { CurrentSessionManager } from "./models/CurrentSession.js";
import { ClientManager } from "./models/Client.js";
import { PropertyManager } from "./models/Property.js";
import { JobManager } from "./models/Job.js";
import { InvoiceManager } from "./models/Invoice.js";
import { LineItemManager } from "./models/LineItem.js";
import { ProfileManager } from "./models/Profile.js";
import { PipelineStageManager } from "./models/PipelineStage.js";
import { WorkItemManager } from "./models/WorkItem.js";
import { WorkItemEventManager } from "./models/WorkItemEvent.js";
import { WorkItemPhotoManager } from "./models/WorkItemPhoto.js";
import { QuoteTemplateManager } from "./models/QuoteTemplate.js";
import { QuoteTemplateItemManager } from "./models/QuoteTemplateItem.js";
import { PaymentMilestoneManager } from "./models/PaymentMilestone.js";
import { MembershipManager } from "./models/Membership.js";
import { ApplianceManager } from "./models/Appliance.js";
import { AmendmentRequestManager } from "./models/AmendmentRequest.js";
import { ComplianceCertificateManager } from "./models/ComplianceCertificate.js";
import { PropertyAccessManager } from "./models/PropertyAccess.js";
import { FeedbackManager } from "./models/Feedback.js";
import { StripeSubscriptionManager } from "./models/StripeSubscription.js";
import { ClientTagAssignmentManager } from "./models/ClientTagAssignment.js";
import { ClientTagManager } from "./models/ClientTag.js";
import { RecurringEnrollmentManager } from "./models/RecurringEnrollment.js";
import { RecurringServiceTypeManager } from "./models/RecurringServiceType.js";
import { PropertyAccessScopeManager } from "./models/PropertyAccessScope.js";
import { PropertyIssueManager } from "./models/PropertyIssue.js";
import { ProfilePublicPhotoManager } from "./models/ProfilePublicPhoto.js";
import { ProfilePublicCategoryManager } from "./models/ProfilePublicCategory.js";
import { PublicEnquiryManager } from "./models/PublicEnquiry.js";
import { ExpenseManager } from "./models/Expense.js";
import { AuditEntryManager } from "./models/AuditEntry.js";
import { ReminderManager } from "./models/Reminder.js";
import { QuoteAttachmentManager } from "./models/QuoteAttachment.js";
import { ProfilePublicServiceManager } from "./models/ProfilePublicService.js";
import { ProductServiceManager } from "./models/ProductService.js";
import { ExternalCalendarFeedManager } from "./models/ExternalCalendarFeed.js";
import { CreditNoteManager } from "./models/CreditNote.js";
import { TradeCategoryManager } from "./models/TradeCategory.js";
import { ContactLinkCandidateManager } from "./models/ContactLinkCandidate.js";
import { UnmatchedCategoryLogManager } from "./models/UnmatchedCategoryLog.js";
import { ReferralManager } from "./models/Referral.js";
import { MembershipInviteManager } from "./models/MembershipInvite.js";
import { SignedAcceptanceManager } from "./models/SignedAcceptance.js";
import { StripePayoutManager } from "./models/StripePayout.js";
import { StripeChargeManager } from "./models/StripeCharge.js";
import { ManosFeeLedgerManager } from "./models/ManosFeeLedger.js";
import { ImportBatchManager } from "./models/ImportBatch.js";
import { SubcontractInviteManager } from "./models/SubcontractInvite.js";
import { AiCreditsTransactionManager } from "./models/AiCreditsTransaction.js";
import { AiModelRateManager } from "./models/AiModelRate.js";
import { AdminNotificationManager } from "./models/AdminNotification.js";
import { PaymentManager } from "./models/Payment.js";
import { PaymentAllocationManager } from "./models/PaymentAllocation.js";
import { AnonymizationEventManager } from "./models/AnonymizationEvent.js";
import { ChaseRuleManager } from "./models/ChaseRule.js";
import { ChaseLogManager } from "./models/ChaseLog.js";
import { AccountingAuditLogManager } from "./models/AccountingAuditLog.js";
import { AccountingConnectionManager } from "./models/AccountingConnection.js";
import { BackfillJobManager } from "./models/BackfillJob.js";
import { ReconcileCursorManager } from "./models/ReconcileCursor.js";
import { PendingCaptureManager } from "./models/PendingCapture.js";
import { UserManager } from "./models/User.js";
import { ApprenticeConversationManager } from "./models/ApprenticeConversation.js";
import { ApprenticeMessageManager } from "./models/ApprenticeMessage.js";
import { ApprenticeResponseManager } from "./models/ApprenticeResponse.js";
import { StripeConnectAccountManager } from "./models/StripeConnectAccount.js";
import { StripeInvoiceManager } from "./models/StripeInvoice.js";
import { StripePaymentMethodManager } from "./models/StripePaymentMethod.js";
import { DefaultSessionSelection as DefaultSessionSelection2 } from "./models/Session.js";
import { DefaultClientSelection as DefaultClientSelection2 } from "./models/Client.js";
import { DefaultPropertySelection as DefaultPropertySelection2 } from "./models/Property.js";
import { DefaultJobSelection as DefaultJobSelection2 } from "./models/Job.js";
import { DefaultInvoiceSelection as DefaultInvoiceSelection2 } from "./models/Invoice.js";
import { DefaultLineItemSelection as DefaultLineItemSelection2 } from "./models/LineItem.js";
import { DefaultProfileSelection as DefaultProfileSelection2 } from "./models/Profile.js";
import { DefaultPipelineStageSelection as DefaultPipelineStageSelection2 } from "./models/PipelineStage.js";
import { DefaultWorkItemSelection as DefaultWorkItemSelection2 } from "./models/WorkItem.js";
import { DefaultWorkItemEventSelection as DefaultWorkItemEventSelection2 } from "./models/WorkItemEvent.js";
import { DefaultWorkItemPhotoSelection as DefaultWorkItemPhotoSelection2 } from "./models/WorkItemPhoto.js";
import { DefaultQuoteTemplateSelection as DefaultQuoteTemplateSelection2 } from "./models/QuoteTemplate.js";
import { DefaultQuoteTemplateItemSelection as DefaultQuoteTemplateItemSelection2 } from "./models/QuoteTemplateItem.js";
import { DefaultPaymentMilestoneSelection as DefaultPaymentMilestoneSelection2 } from "./models/PaymentMilestone.js";
import { DefaultMembershipSelection as DefaultMembershipSelection2 } from "./models/Membership.js";
import { DefaultApplianceSelection as DefaultApplianceSelection2 } from "./models/Appliance.js";
import { DefaultAmendmentRequestSelection as DefaultAmendmentRequestSelection2 } from "./models/AmendmentRequest.js";
import { DefaultComplianceCertificateSelection as DefaultComplianceCertificateSelection2 } from "./models/ComplianceCertificate.js";
import { DefaultPropertyAccessSelection as DefaultPropertyAccessSelection2 } from "./models/PropertyAccess.js";
import { DefaultFeedbackSelection as DefaultFeedbackSelection2 } from "./models/Feedback.js";
import { DefaultStripeSubscriptionSelection as DefaultStripeSubscriptionSelection2 } from "./models/StripeSubscription.js";
import { DefaultClientTagAssignmentSelection as DefaultClientTagAssignmentSelection2 } from "./models/ClientTagAssignment.js";
import { DefaultClientTagSelection as DefaultClientTagSelection2 } from "./models/ClientTag.js";
import { DefaultRecurringEnrollmentSelection as DefaultRecurringEnrollmentSelection2 } from "./models/RecurringEnrollment.js";
import { DefaultRecurringServiceTypeSelection as DefaultRecurringServiceTypeSelection2 } from "./models/RecurringServiceType.js";
import { DefaultPropertyAccessScopeSelection as DefaultPropertyAccessScopeSelection2 } from "./models/PropertyAccessScope.js";
import { DefaultPropertyIssueSelection as DefaultPropertyIssueSelection2 } from "./models/PropertyIssue.js";
import { DefaultProfilePublicPhotoSelection as DefaultProfilePublicPhotoSelection2 } from "./models/ProfilePublicPhoto.js";
import { DefaultProfilePublicCategorySelection as DefaultProfilePublicCategorySelection2 } from "./models/ProfilePublicCategory.js";
import { DefaultPublicEnquirySelection as DefaultPublicEnquirySelection2 } from "./models/PublicEnquiry.js";
import { DefaultExpenseSelection as DefaultExpenseSelection2 } from "./models/Expense.js";
import { DefaultAuditEntrySelection as DefaultAuditEntrySelection2 } from "./models/AuditEntry.js";
import { DefaultReminderSelection as DefaultReminderSelection2 } from "./models/Reminder.js";
import { DefaultQuoteAttachmentSelection as DefaultQuoteAttachmentSelection2 } from "./models/QuoteAttachment.js";
import { DefaultProfilePublicServiceSelection as DefaultProfilePublicServiceSelection2 } from "./models/ProfilePublicService.js";
import { DefaultProductServiceSelection as DefaultProductServiceSelection2 } from "./models/ProductService.js";
import { DefaultExternalCalendarFeedSelection as DefaultExternalCalendarFeedSelection2 } from "./models/ExternalCalendarFeed.js";
import { DefaultCreditNoteSelection as DefaultCreditNoteSelection2 } from "./models/CreditNote.js";
import { DefaultTradeCategorySelection as DefaultTradeCategorySelection2 } from "./models/TradeCategory.js";
import { DefaultContactLinkCandidateSelection as DefaultContactLinkCandidateSelection2 } from "./models/ContactLinkCandidate.js";
import { DefaultUnmatchedCategoryLogSelection as DefaultUnmatchedCategoryLogSelection2 } from "./models/UnmatchedCategoryLog.js";
import { DefaultReferralSelection as DefaultReferralSelection2 } from "./models/Referral.js";
import { DefaultMembershipInviteSelection as DefaultMembershipInviteSelection2 } from "./models/MembershipInvite.js";
import { DefaultSignedAcceptanceSelection as DefaultSignedAcceptanceSelection2 } from "./models/SignedAcceptance.js";
import { DefaultStripePayoutSelection as DefaultStripePayoutSelection2 } from "./models/StripePayout.js";
import { DefaultStripeChargeSelection as DefaultStripeChargeSelection2 } from "./models/StripeCharge.js";
import { DefaultManosFeeLedgerSelection as DefaultManosFeeLedgerSelection2 } from "./models/ManosFeeLedger.js";
import { DefaultImportBatchSelection as DefaultImportBatchSelection2 } from "./models/ImportBatch.js";
import { DefaultSubcontractInviteSelection as DefaultSubcontractInviteSelection2 } from "./models/SubcontractInvite.js";
import { DefaultAiCreditsTransactionSelection as DefaultAiCreditsTransactionSelection2 } from "./models/AiCreditsTransaction.js";
import { DefaultAiModelRateSelection as DefaultAiModelRateSelection2 } from "./models/AiModelRate.js";
import { DefaultAdminNotificationSelection as DefaultAdminNotificationSelection2 } from "./models/AdminNotification.js";
import { DefaultPaymentSelection as DefaultPaymentSelection2 } from "./models/Payment.js";
import { DefaultPaymentAllocationSelection as DefaultPaymentAllocationSelection2 } from "./models/PaymentAllocation.js";
import { DefaultAnonymizationEventSelection as DefaultAnonymizationEventSelection2 } from "./models/AnonymizationEvent.js";
import { DefaultChaseRuleSelection as DefaultChaseRuleSelection2 } from "./models/ChaseRule.js";
import { DefaultChaseLogSelection as DefaultChaseLogSelection2 } from "./models/ChaseLog.js";
import { DefaultAccountingAuditLogSelection as DefaultAccountingAuditLogSelection2 } from "./models/AccountingAuditLog.js";
import { DefaultAccountingConnectionSelection as DefaultAccountingConnectionSelection2 } from "./models/AccountingConnection.js";
import { DefaultBackfillJobSelection as DefaultBackfillJobSelection2 } from "./models/BackfillJob.js";
import { DefaultReconcileCursorSelection as DefaultReconcileCursorSelection2 } from "./models/ReconcileCursor.js";
import { DefaultPendingCaptureSelection as DefaultPendingCaptureSelection2 } from "./models/PendingCapture.js";
import { DefaultUserSelection as DefaultUserSelection2 } from "./models/User.js";
import { DefaultApprenticeConversationSelection as DefaultApprenticeConversationSelection2 } from "./models/ApprenticeConversation.js";
import { DefaultApprenticeMessageSelection as DefaultApprenticeMessageSelection2 } from "./models/ApprenticeMessage.js";
import { DefaultApprenticeResponseSelection as DefaultApprenticeResponseSelection2 } from "./models/ApprenticeResponse.js";
import { DefaultStripeConnectAccountSelection as DefaultStripeConnectAccountSelection2 } from "./models/StripeConnectAccount.js";
import { DefaultStripeInvoiceSelection as DefaultStripeInvoiceSelection2 } from "./models/StripeInvoice.js";
import { DefaultStripePaymentMethodSelection as DefaultStripePaymentMethodSelection2 } from "./models/StripePaymentMethod.js";
const productionEnv = "production";
const fallbackEnv = "development";
const availableAuthenticationModes = [
  "apiKey",
  "browserSession",
  "anonymous",
  "internalAuthToken",
  "internal",
  "custom"
];
const maybeGetAuthenticationModeOptionsFromClientOptions = (options) => {
  const topLevelAuthModes = {};
  for (const key of availableAuthenticationModes) {
    if (key in options) {
      topLevelAuthModes[key] = options[key];
    }
  }
  if ("authenticationMode" in options && Object.keys(topLevelAuthModes).length > 0) {
    throw new GadgetClientError(
      "Declaring authentication modes at the top level and under the `authenticationMode` key at the same time is not allowed."
    );
  }
  if ("authenticationMode" in options) {
    return options.authenticationMode;
  }
  if (Object.keys(topLevelAuthModes).length === 0) {
    return void 0;
  }
  return topLevelAuthModes;
};
const getImplicitEnv = () => {
  try {
    return process.env.GADGET_ENV;
  } catch (error) {
    return void 0;
  }
};
class ManosClient {
  constructor(options) {
    this.$args = Symbol.for("gadget/fieldArgs");
    /** Executes the anonymizeSubject global action. */
    this.anonymizeSubject = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "anonymizeSubject",
      operationName: "anonymizeSubject",
      operationReturnType: "AnonymizeSubject",
      namespace: null,
      variables: {}
    });
    /** Executes the applyXeroWebhookEvent global action. */
    this.applyXeroWebhookEvent = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "applyXeroWebhookEvent",
      operationName: "applyXeroWebhookEvent",
      operationReturnType: "ApplyXeroWebhookEvent",
      namespace: null,
      variables: {
        connectionId: { required: false, type: "String" },
        tenantId: { required: false, type: "String" },
        resourceType: { required: false, type: "String" },
        resourceId: { required: false, type: "String" },
        changeType: { required: false, type: "String" },
        occurredAt: { required: false, type: "String" },
        raw: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the attachPaymentMethod global action. */
    this.attachPaymentMethod = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "attachPaymentMethod",
      operationName: "attachPaymentMethod",
      operationReturnType: "AttachPaymentMethod",
      namespace: null,
      variables: {}
    });
    /** Executes the attributeReferral global action. */
    this.attributeReferral = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "attributeReferral",
      operationName: "attributeReferral",
      operationReturnType: "AttributeReferral",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillChaseRules global action. */
    this.backfillChaseRules = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillChaseRules",
      operationName: "backfillChaseRules",
      operationReturnType: "BackfillChaseRules",
      namespace: null,
      variables: { profileId: { required: false, type: "String" } }
    });
    /** Executes the backfillClientType global action. */
    this.backfillClientType = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillClientType",
      operationName: "backfillClientType",
      operationReturnType: "BackfillClientType",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillClientVisibility global action. */
    this.backfillClientVisibility = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillClientVisibility",
      operationName: "backfillClientVisibility",
      operationReturnType: "BackfillClientVisibility",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillFilesToR2 global action. */
    this.backfillFilesToR2 = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillFilesToR2",
      operationName: "backfillFilesToR2",
      operationReturnType: "BackfillFilesToR2",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillProfileSchedulingDefaults global action. */
    this.backfillProfileSchedulingDefaults = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillProfileSchedulingDefaults",
      operationName: "backfillProfileSchedulingDefaults",
      operationReturnType: "BackfillProfileSchedulingDefaults",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillScheduledStatus global action. */
    this.backfillScheduledStatus = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillScheduledStatus",
      operationName: "backfillScheduledStatus",
      operationReturnType: "BackfillScheduledStatus",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillStandaloneErrandsToJobs global action. */
    this.backfillStandaloneErrandsToJobs = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillStandaloneErrandsToJobs",
      operationName: "backfillStandaloneErrandsToJobs",
      operationReturnType: "BackfillStandaloneErrandsToJobs",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillTradeCategoryFKs global action. */
    this.backfillTradeCategoryFKs = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillTradeCategoryFKs",
      operationName: "backfillTradeCategoryFKs",
      operationReturnType: "BackfillTradeCategoryFKs",
      namespace: null,
      variables: {}
    });
    /** Executes the backfillUserStripeData global action. */
    this.backfillUserStripeData = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "backfillUserStripeData",
      operationName: "backfillUserStripeData",
      operationReturnType: "BackfillUserStripeData",
      namespace: null,
      variables: {}
    });
    /** Executes the cancelMySubscription global action. */
    this.cancelMySubscription = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "cancelMySubscription",
      operationName: "cancelMySubscription",
      operationReturnType: "CancelMySubscription",
      namespace: null,
      variables: {}
    });
    /** Executes the clearSampleData global action. */
    this.clearSampleData = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "clearSampleData",
      operationName: "clearSampleData",
      operationReturnType: "ClearSampleData",
      namespace: null,
      variables: {}
    });
    /** Executes the createConnectAccount global action. */
    this.createConnectAccount = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createConnectAccount",
      operationName: "createConnectAccount",
      operationReturnType: "CreateConnectAccount",
      namespace: null,
      variables: {}
    });
    /** Executes the createConnectAccountSession global action. */
    this.createConnectAccountSession = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createConnectAccountSession",
      operationName: "createConnectAccountSession",
      operationReturnType: "CreateConnectAccountSession",
      namespace: null,
      variables: {}
    });
    /** Executes the createSetupIntent global action. */
    this.createSetupIntent = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "createSetupIntent",
      operationName: "createSetupIntent",
      operationReturnType: "CreateSetupIntent",
      namespace: null,
      variables: {}
    });
    /** Executes the deleteAccount global action. */
    this.deleteAccount = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "deleteAccount",
      operationName: "deleteAccount",
      operationReturnType: "DeleteAccount",
      namespace: null,
      variables: {}
    });
    /** Executes the detachPaymentMethod global action. */
    this.detachPaymentMethod = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "detachPaymentMethod",
      operationName: "detachPaymentMethod",
      operationReturnType: "DetachPaymentMethod",
      namespace: null,
      variables: {}
    });
    /** Executes the devResetUserTrial global action. */
    this.devResetUserTrial = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "devResetUserTrial",
      operationName: "devResetUserTrial",
      operationReturnType: "DevResetUserTrial",
      namespace: null,
      variables: { email: { required: false, type: "String" } }
    });
    /** Executes the enrichBusinessFromIdentifier global action. */
    this.enrichBusinessFromIdentifier = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "enrichBusinessFromIdentifier",
      operationName: "enrichBusinessFromIdentifier",
      operationReturnType: "EnrichBusinessFromIdentifier",
      namespace: null,
      variables: {
        businessName: { required: false, type: "String" },
        website: { required: false, type: "String" },
        companyNumber: { required: false, type: "String" },
        country: { required: false, type: "String" }
      }
    });
    /** Executes the evaluateChaseRules global action. */
    this.evaluateChaseRules = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "evaluateChaseRules",
      operationName: "evaluateChaseRules",
      operationReturnType: "EvaluateChaseRules",
      namespace: null,
      variables: {}
    });
    /** Executes the exportProfileSnapshot global action. */
    this.exportProfileSnapshot = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "exportProfileSnapshot",
      operationName: "exportProfileSnapshot",
      operationReturnType: "ExportProfileSnapshot",
      namespace: null,
      variables: {
        profileId: { required: false, type: "String" },
        includeAuditEntries: { required: false, type: "Boolean" }
      }
    });
    /** Executes the extractReceiptStructure global action. */
    this.extractReceiptStructure = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "extractReceiptStructure",
      operationName: "extractReceiptStructure",
      operationReturnType: "ExtractReceiptStructure",
      namespace: null,
      variables: { workItemPhotoId: { required: false, type: "String" } }
    });
    /** Executes the findPhotosByLabel global action. */
    this.findPhotosByLabel = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "findPhotosByLabel",
      operationName: "findPhotosByLabel",
      operationReturnType: "FindPhotosByLabel",
      namespace: null,
      variables: {
        label: { required: false, type: "String" },
        scope: { required: false, type: "String" },
        workItemId: { required: false, type: "String" },
        limit: { required: false, type: "Float" }
      }
    });
    /** Executes the getAiCreditsTeamBreakdown global action. */
    this.getAiCreditsTeamBreakdown = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getAiCreditsTeamBreakdown",
      operationName: "getAiCreditsTeamBreakdown",
      operationReturnType: "GetAiCreditsTeamBreakdown",
      namespace: null,
      variables: {}
    });
    /** Executes the getInboxCounters global action. */
    this.getInboxCounters = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getInboxCounters",
      operationName: "getInboxCounters",
      operationReturnType: "GetInboxCounters",
      namespace: null,
      variables: {}
    });
    /** Executes the getInboxHomeScreen global action. */
    this.getInboxHomeScreen = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getInboxHomeScreen",
      operationName: "getInboxHomeScreen",
      operationReturnType: "GetInboxHomeScreen",
      namespace: null,
      variables: {}
    });
    /** Executes the getOverviewHeartbeat global action. */
    this.getOverviewHeartbeat = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getOverviewHeartbeat",
      operationName: "getOverviewHeartbeat",
      operationReturnType: "GetOverviewHeartbeat",
      namespace: null,
      variables: {}
    });
    /** Executes the getUpcomingSummary global action. */
    this.getUpcomingSummary = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "getUpcomingSummary",
      operationName: "getUpcomingSummary",
      operationReturnType: "GetUpcomingSummary",
      namespace: null,
      variables: {}
    });
    /** Executes the grantCompPlan global action. */
    this.grantCompPlan = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "grantCompPlan",
      operationName: "grantCompPlan",
      operationReturnType: "GrantCompPlan",
      namespace: null,
      variables: {}
    });
    /** Executes the importClientsFromSpreadsheet global action. */
    this.importClientsFromSpreadsheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "importClientsFromSpreadsheet",
      operationName: "importClientsFromSpreadsheet",
      operationReturnType: "ImportClientsFromSpreadsheet",
      namespace: null,
      variables: {
        rows: { required: false, type: "[JSONObject!]" },
        profileId: { required: false, type: "String" }
      }
    });
    /** Executes the importContacts global action. */
    this.importContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "importContacts",
      operationName: "importContacts",
      operationReturnType: "ImportContacts",
      namespace: null,
      variables: {
        rows: { required: false, type: "[JSONObject!]" },
        profileId: { required: false, type: "String" },
        conflictStrategy: { required: false, type: "String" }
      }
    });
    /** Executes the importInvoicesFromSpreadsheet global action. */
    this.importInvoicesFromSpreadsheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "importInvoicesFromSpreadsheet",
      operationName: "importInvoicesFromSpreadsheet",
      operationReturnType: "ImportInvoicesFromSpreadsheet",
      namespace: null,
      variables: {
        rows: { required: false, type: "[JSONObject!]" },
        profileId: { required: false, type: "String" }
      }
    });
    /** Executes the importWorkItemsFromSpreadsheet global action. */
    this.importWorkItemsFromSpreadsheet = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "importWorkItemsFromSpreadsheet",
      operationName: "importWorkItemsFromSpreadsheet",
      operationReturnType: "ImportWorkItemsFromSpreadsheet",
      namespace: null,
      variables: {
        rows: { required: false, type: "[JSONObject!]" },
        profileId: { required: false, type: "String" }
      }
    });
    /** Executes the inviteTeamMember global action. */
    this.inviteTeamMember = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "inviteTeamMember",
      operationName: "inviteTeamMember",
      operationReturnType: "InviteTeamMember",
      namespace: null,
      variables: {
        email: { required: false, type: "String" },
        role: { required: false, type: "String" },
        profileId: { required: false, type: "String" }
      }
    });
    /** Executes the loadSampleData global action. */
    this.loadSampleData = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "loadSampleData",
      operationName: "loadSampleData",
      operationReturnType: "LoadSampleData",
      namespace: null,
      variables: {
        tradeType: { required: false, type: "String" },
        scale: { required: false, type: "String" }
      }
    });
    /** Executes the markAdminNotificationsRead global action. */
    this.markAdminNotificationsRead = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "markAdminNotificationsRead",
      operationName: "markAdminNotificationsRead",
      operationReturnType: "MarkAdminNotificationsRead",
      namespace: null,
      variables: {}
    });
    /** Executes the markUserTourComplete global action. */
    this.markUserTourComplete = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "markUserTourComplete",
      operationName: "markUserTourComplete",
      operationReturnType: "MarkUserTourComplete",
      namespace: null,
      variables: { tourKey: { required: false, type: "String" } }
    });
    /** Executes the markUserToursDisabled global action. */
    this.markUserToursDisabled = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "markUserToursDisabled",
      operationName: "markUserToursDisabled",
      operationReturnType: "MarkUserToursDisabled",
      namespace: null,
      variables: {}
    });
    /** Executes the migrateAccountingTokens global action. */
    this.migrateAccountingTokens = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "migrateAccountingTokens",
      operationName: "migrateAccountingTokens",
      operationReturnType: "MigrateAccountingTokens",
      namespace: null,
      variables: {}
    });
    /** Executes the pollExternalCalendarFeeds global action. */
    this.pollExternalCalendarFeeds = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "pollExternalCalendarFeeds",
      operationName: "pollExternalCalendarFeeds",
      operationReturnType: "PollExternalCalendarFeeds",
      namespace: null,
      variables: {}
    });
    /** Executes the processBackfillJobs global action. */
    this.processBackfillJobs = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processBackfillJobs",
      operationName: "processBackfillJobs",
      operationReturnType: "ProcessBackfillJobs",
      namespace: null,
      variables: {}
    });
    /** Executes the processCapture global action. */
    this.processCapture = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processCapture",
      operationName: "processCapture",
      operationReturnType: "ProcessCapture",
      namespace: null,
      variables: { pendingCaptureId: { required: false, type: "String" } }
    });
    /** Executes the processOneCalendarFeed global action. */
    this.processOneCalendarFeed = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processOneCalendarFeed",
      operationName: "processOneCalendarFeed",
      operationReturnType: "ProcessOneCalendarFeed",
      namespace: null,
      variables: { id: { required: false, type: "String" } }
    });
    /** Executes the processOneChase global action. */
    this.processOneChase = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processOneChase",
      operationName: "processOneChase",
      operationReturnType: "ProcessOneChase",
      namespace: null,
      variables: {
        workItemId: { required: false, type: "String" },
        chaseRuleId: { required: false, type: "String" }
      }
    });
    /** Executes the processOneImportRow global action. */
    this.processOneImportRow = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processOneImportRow",
      operationName: "processOneImportRow",
      operationReturnType: "ProcessOneImportRow",
      namespace: null,
      variables: {
        batchId: { required: false, type: "String" },
        rowIndex: { required: false, type: "Float" },
        payload: { required: false, type: "JSONObject" }
      }
    });
    /** Executes the processOneReminder global action. */
    this.processOneReminder = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processOneReminder",
      operationName: "processOneReminder",
      operationReturnType: "ProcessOneReminder",
      namespace: null,
      variables: { id: { required: false, type: "String" } }
    });
    /** Executes the processQuickBooksWebhookEvents global action. */
    this.processQuickBooksWebhookEvents = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processQuickBooksWebhookEvents",
      operationName: "processQuickBooksWebhookEvents",
      operationReturnType: "ProcessQuickBooksWebhookEvents",
      namespace: null,
      variables: {
        connectionId: { required: false, type: "String" },
        events: { required: false, type: "[JSONObject!]" }
      }
    });
    /** Executes the processReminders global action. */
    this.processReminders = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "processReminders",
      operationName: "processReminders",
      operationReturnType: "ProcessReminders",
      namespace: null,
      variables: {}
    });
    /** Executes the pushSubscriptionInvoiceToPlatformXero global action. */
    this.pushSubscriptionInvoiceToPlatformXero = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "pushSubscriptionInvoiceToPlatformXero",
      operationName: "pushSubscriptionInvoiceToPlatformXero",
      operationReturnType: "PushSubscriptionInvoiceToPlatformXero",
      namespace: null,
      variables: {}
    });
    /** Executes the reSyncLinkedContacts global action. */
    this.reSyncLinkedContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "reSyncLinkedContacts",
      operationName: "reSyncLinkedContacts",
      operationReturnType: "ReSyncLinkedContacts",
      namespace: null,
      variables: {
        linkedProfileId: { required: false, type: "String" },
        profileId: { required: false, type: "String" }
      }
    });
    /** Executes the resetApprenticeBundles global action. */
    this.resetApprenticeBundles = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "resetApprenticeBundles",
      operationName: "resetApprenticeBundles",
      operationReturnType: "ResetApprenticeBundles",
      namespace: null,
      variables: {}
    });
    /** Executes the revokeCompPlan global action. */
    this.revokeCompPlan = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "revokeCompPlan",
      operationName: "revokeCompPlan",
      operationReturnType: "RevokeCompPlan",
      namespace: null,
      variables: {}
    });
    /** Executes the runApprenticeCompletion global action. */
    this.runApprenticeCompletion = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "runApprenticeCompletion",
      operationName: "runApprenticeCompletion",
      operationReturnType: "RunApprenticeCompletion",
      namespace: null,
      variables: {}
    });
    /** Executes the seedTestContacts global action. */
    this.seedTestContacts = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "seedTestContacts",
      operationName: "seedTestContacts",
      operationReturnType: "SeedTestContacts",
      namespace: null,
      variables: {
        userEmail: { required: false, type: "String" },
        count: { required: false, type: "Float" }
      }
    });
    /** Executes the seedTradeCategories global action. */
    this.seedTradeCategories = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "seedTradeCategories",
      operationName: "seedTradeCategories",
      operationReturnType: "SeedTradeCategories",
      namespace: null,
      variables: {}
    });
    /** Executes the seedTradeShowcase global action. */
    this.seedTradeShowcase = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "seedTradeShowcase",
      operationName: "seedTradeShowcase",
      operationReturnType: "SeedTradeShowcase",
      namespace: null,
      variables: {
        tradeType: { required: false, type: "String" },
        profileId: { required: false, type: "String" },
        scale: { required: false, type: "String" },
        seed: { required: false, type: "Float" }
      }
    });
    /** Executes the sendApprenticeMessage global action. */
    this.sendApprenticeMessage = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sendApprenticeMessage",
      operationName: "sendApprenticeMessage",
      operationReturnType: "SendApprenticeMessage",
      namespace: null,
      variables: {}
    });
    /** Executes the setDefaultPaymentMethod global action. */
    this.setDefaultPaymentMethod = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setDefaultPaymentMethod",
      operationName: "setDefaultPaymentMethod",
      operationReturnType: "SetDefaultPaymentMethod",
      namespace: null,
      variables: {}
    });
    /** Executes the setSidebarCollapsed global action. */
    this.setSidebarCollapsed = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setSidebarCollapsed",
      operationName: "setSidebarCollapsed",
      operationReturnType: "SetSidebarCollapsed",
      namespace: null,
      variables: { collapsed: { required: false, type: "Boolean" } }
    });
    /** Executes the setupRewardCoupons global action. */
    this.setupRewardCoupons = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setupRewardCoupons",
      operationName: "setupRewardCoupons",
      operationReturnType: "SetupRewardCoupons",
      namespace: null,
      variables: {}
    });
    /** Executes the setupTrialCoupons global action. */
    this.setupTrialCoupons = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "setupTrialCoupons",
      operationName: "setupTrialCoupons",
      operationReturnType: "SetupTrialCoupons",
      namespace: null,
      variables: {}
    });
    /** Executes the startAccountingBackfill global action. */
    this.startAccountingBackfill = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "startAccountingBackfill",
      operationName: "startAccountingBackfill",
      operationReturnType: "StartAccountingBackfill",
      namespace: null,
      variables: {}
    });
    /** Executes the sweepExpiredMembershipInvites global action. */
    this.sweepExpiredMembershipInvites = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sweepExpiredMembershipInvites",
      operationName: "sweepExpiredMembershipInvites",
      operationReturnType: "SweepExpiredMembershipInvites",
      namespace: null,
      variables: {}
    });
    /** Executes the sweepExpiredQuotes global action. */
    this.sweepExpiredQuotes = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sweepExpiredQuotes",
      operationName: "sweepExpiredQuotes",
      operationReturnType: "SweepExpiredQuotes",
      namespace: null,
      variables: {}
    });
    /** Executes the sweepExpiredTentatives global action. */
    this.sweepExpiredTentatives = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "sweepExpiredTentatives",
      operationName: "sweepExpiredTentatives",
      operationReturnType: "SweepExpiredTentatives",
      namespace: null,
      variables: {}
    });
    /** Executes the syncApprenticeModelRates global action. */
    this.syncApprenticeModelRates = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "syncApprenticeModelRates",
      operationName: "syncApprenticeModelRates",
      operationReturnType: "SyncApprenticeModelRates",
      namespace: null,
      variables: {}
    });
    /** Executes the testSeedPersona global action. */
    this.testSeedPersona = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testSeedPersona",
      operationName: "testSeedPersona",
      operationReturnType: "TestSeedPersona",
      namespace: null,
      variables: { personaKey: { required: false, type: "String" } }
    });
    /** Executes the testSeedReferralReward global action. */
    this.testSeedReferralReward = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "testSeedReferralReward",
      operationName: "testSeedReferralReward",
      operationReturnType: "TestSeedReferralReward",
      namespace: null,
      variables: {
        mode: { required: false, type: "String" },
        referralId: { required: false, type: "String" },
        referrerEmail: { required: false, type: "String" },
        referredEmail: { required: false, type: "String" },
        source: { required: false, type: "String" },
        convertedAt: { required: false, type: "String" },
        rewardAppliedAt: { required: false, type: "String" },
        rewardType: { required: false, type: "String" }
      }
    });
    /** Executes the toggleDirectoryListing global action. */
    this.toggleDirectoryListing = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "toggleDirectoryListing",
      operationName: "toggleDirectoryListing",
      operationReturnType: "ToggleDirectoryListing",
      namespace: null,
      variables: {
        profileId: { required: false, type: "String" },
        listed: { required: false, type: "Boolean" }
      }
    });
    /** Executes the updatePublicListing global action. */
    this.updatePublicListing = buildGlobalAction(this, {
      type: "globalAction",
      functionName: "updatePublicListing",
      operationName: "updatePublicListing",
      operationReturnType: "UpdatePublicListing",
      namespace: null,
      variables: {
        profileId: { required: false, type: "String" },
        bio: { required: false, type: "String" },
        slug: { required: false, type: "String" },
        coverageRadiusMiles: { required: false, type: "Float" },
        postcode: { required: false, type: "String" },
        categories: {
          required: false,
          type: "[UpdatePublicListingCategoriesElementTypeInput!]"
        }
      }
    });
    /** Executes the profileSyncHead computed view. */
    this.profileSyncHead = buildComputedView(this, {
      type: "computedView",
      operationName: "profileSyncHead",
      functionName: "profileSyncHead",
      gqlFieldName: "profileSyncHead",
      referencedModelKeys: [
        "Tc0uUnzYhFFt",
        "KknBxefIGqiP",
        "j5xzekPYGVvr",
        "obOFgvJosD0A",
        "LXHKgsTNhsa7",
        "clientTag_v1",
        "clientTagAssignment_v1",
        "gHWn-5sOvbl6",
        "DataModel-CreditNote",
        "DataModel-Expense",
        "khzutWzekJ2p",
        "DataModel-Reminder",
        "E7Jv4J_PnpY3",
        "ps_model",
        "NtF44QG_gGcb",
        "qa_schema_v1",
        "recurringEnrollment_v1",
        "recurringServiceType_v1",
        "mem_membership_01",
        "DataModel-MembershipInvite",
        "DataModel-SubcontractInvite",
        "signedAcceptance",
        "ext_cal_feed_01",
        "ljlDRarLOqRu",
        "referral_v1",
        "zJSS2Nqi2m7O",
        "sch_charge_01",
        "spo_payout_01",
        "sub_subscription_01",
        "cuFUjr_nQWmg",
        "Uq9hjr3RmZ8d",
        "pa_model",
        "pas_model",
        "pi_model",
        "app_model",
        "cc_model",
        "DataModel-Payment",
        "DataModel-PaymentAllocation",
        "DataModel-ChaseRule",
        "DataModel-ChaseLog"
      ],
      referencedTypenames: [
        "WorkItem",
        "Profile",
        "WorkItemEvent",
        "WorkItemPhoto",
        "Client",
        "ClientTag",
        "ClientTagAssignment",
        "Invoice",
        "CreditNote",
        "Expense",
        "PaymentMilestone",
        "Reminder",
        "PipelineStage",
        "ProductService",
        "QuoteTemplate",
        "QuoteAttachment",
        "RecurringEnrollment",
        "RecurringServiceType",
        "Membership",
        "MembershipInvite",
        "SubcontractInvite",
        "SignedAcceptance",
        "ExternalCalendarFeed",
        "Property",
        "Referral",
        "Job",
        "StripeCharge",
        "StripePayout",
        "StripeSubscription",
        "LineItem",
        "QuoteTemplateItem",
        "PropertyAccess",
        "PropertyAccessScope",
        "PropertyIssue",
        "Appliance",
        "ComplianceCertificate",
        "Payment",
        "PaymentAllocation",
        "ChaseRule",
        "ChaseLog"
      ],
      namespace: null,
      variables: { profileId: { required: false, type: "String" } }
    });
    /** Executes an inline computed view. */
    this.view = buildInlineComputedView(this, {
      type: "computedView",
      operationName: "gellyView",
      functionName: "view",
      gqlFieldName: "gellyView",
      namespace: null,
      variables: {
        query: { type: "String", required: true },
        args: { type: "JSONObject" }
      }
    });
    /**
     * The list of environments with a customized API root endpoint
     */
    this.apiRoots = { "development": "https://manos--development.gadget.app/", "production": "https://manos.team/" };
    this.applicationId = "319042";
    /** Start a transaction against the Gadget backend which will atomically commit (or rollback). */
    this.transaction = async (callback) => {
      return await this.connection.transaction(callback);
    };
    /**
    * Get a new direct upload token for file uploads to directly to cloud storage.
    * See https://docs.gadget.dev/guides/storing-files#direct-uploads-using-tokens for more information
    * @return { url: string, token: string } A `url` to upload one file to, and a token for that file to pass back to Gadget as an action input.
    */
    this.getDirectUploadToken = async () => {
      const result = await this.query("query GetDirectUploadToken($nonce: String) { gadgetMeta { directUploadToken(nonce: $nonce) { url, token } } }", { nonce: Math.random().toString(36).slice(2, 7) }, {
        requestPolicy: "network-only"
      });
      return result.gadgetMeta.directUploadToken;
    };
    let inSSRContext = false;
    this.options = options;
    try {
      inSSRContext = !!(import.meta.env && import.meta.env.SSR);
    } catch (error) {
    }
    if (inSSRContext) {
      const api = globalThis.GadgetGlobals?.api;
      if (api) {
        return api.actAsSession;
      }
    }
    this.environment = (options?.environment ?? getImplicitEnv() ?? fallbackEnv).toLocaleLowerCase();
    let apiRoot;
    if (this.apiRoots[this.environment]) {
      apiRoot = this.apiRoots[this.environment];
    } else {
      const envPart = this.environment == productionEnv ? "" : `--${this.environment}`;
      apiRoot = `https://manos${envPart}.gadget.app`;
    }
    const exchanges = { ...options?.exchanges };
    if (this.environment !== productionEnv) {
      const devHarnessExchange = ({ forward }) => {
        return (operations$) => {
          const operationResult$ = forward(operations$);
          return pipe(
            operationResult$,
            map((result) => {
              try {
                if (typeof window !== "undefined" && typeof CustomEvent === "function") {
                  const event = new CustomEvent("gadget:devharness:graphqlresult", { detail: result });
                  window.dispatchEvent(event);
                }
              } catch (error) {
                console.warn("[gadget] error dispatching gadget dev harness event", error);
              }
              return result;
            })
          );
        };
      };
      exchanges.beforeAll = [
        devHarnessExchange,
        ...exchanges.beforeAll ?? []
      ];
    }
    const connectionOptions = {
      endpoint: new URL("api/graphql", apiRoot).toString(),
      applicationId: this.applicationId,
      authenticationMode: options?.authenticationMode,
      ...options,
      exchanges,
      environment: this.environment
    };
    const authenticationMode = maybeGetAuthenticationModeOptionsFromClientOptions(options ?? {});
    connectionOptions.authenticationMode = authenticationMode ?? (typeof window == "undefined" ? { anonymous: true } : { browserSession: true });
    this.connection = new GadgetConnection(connectionOptions);
    if (typeof window != "undefined" && typeof window.document != "undefined" && this.connection.authenticationMode == AuthenticationMode.APIKey && !options?.authenticationMode?.dangerouslyAllowBrowserApiKey) {
      throw new Error("GGT_BROWSER_API_KEY_USAGE: Using a Gadget API key to authenticate this client object is insecure and will leak your API keys to attackers. Please use a different authentication mode.");
    }
    this.session = new SessionManager(this.connection);
    this.currentSession = new CurrentSessionManager(this.connection);
    this.client = new ClientManager(this.connection);
    this.property = new PropertyManager(this.connection);
    this.job = new JobManager(this.connection);
    this.invoice = new InvoiceManager(this.connection);
    this.lineItem = new LineItemManager(this.connection);
    this.profile = new ProfileManager(this.connection);
    this.pipelineStage = new PipelineStageManager(this.connection);
    this.workItem = new WorkItemManager(this.connection);
    this.workItemEvent = new WorkItemEventManager(this.connection);
    this.workItemPhoto = new WorkItemPhotoManager(this.connection);
    this.quoteTemplate = new QuoteTemplateManager(this.connection);
    this.quoteTemplateItem = new QuoteTemplateItemManager(this.connection);
    this.paymentMilestone = new PaymentMilestoneManager(this.connection);
    this.membership = new MembershipManager(this.connection);
    this.appliance = new ApplianceManager(this.connection);
    this.amendmentRequest = new AmendmentRequestManager(this.connection);
    this.complianceCertificate = new ComplianceCertificateManager(this.connection);
    this.propertyAccess = new PropertyAccessManager(this.connection);
    this.feedback = new FeedbackManager(this.connection);
    this.stripeSubscription = new StripeSubscriptionManager(this.connection);
    this.clientTagAssignment = new ClientTagAssignmentManager(this.connection);
    this.clientTag = new ClientTagManager(this.connection);
    this.recurringEnrollment = new RecurringEnrollmentManager(this.connection);
    this.recurringServiceType = new RecurringServiceTypeManager(this.connection);
    this.propertyAccessScope = new PropertyAccessScopeManager(this.connection);
    this.propertyIssue = new PropertyIssueManager(this.connection);
    this.profilePublicPhoto = new ProfilePublicPhotoManager(this.connection);
    this.profilePublicCategory = new ProfilePublicCategoryManager(this.connection);
    this.publicEnquiry = new PublicEnquiryManager(this.connection);
    this.expense = new ExpenseManager(this.connection);
    this.auditEntry = new AuditEntryManager(this.connection);
    this.reminder = new ReminderManager(this.connection);
    this.quoteAttachment = new QuoteAttachmentManager(this.connection);
    this.profilePublicService = new ProfilePublicServiceManager(this.connection);
    this.productService = new ProductServiceManager(this.connection);
    this.externalCalendarFeed = new ExternalCalendarFeedManager(this.connection);
    this.creditNote = new CreditNoteManager(this.connection);
    this.tradeCategory = new TradeCategoryManager(this.connection);
    this.contactLinkCandidate = new ContactLinkCandidateManager(this.connection);
    this.unmatchedCategoryLog = new UnmatchedCategoryLogManager(this.connection);
    this.referral = new ReferralManager(this.connection);
    this.membershipInvite = new MembershipInviteManager(this.connection);
    this.signedAcceptance = new SignedAcceptanceManager(this.connection);
    this.stripePayout = new StripePayoutManager(this.connection);
    this.stripeCharge = new StripeChargeManager(this.connection);
    this.manosFeeLedger = new ManosFeeLedgerManager(this.connection);
    this.importBatch = new ImportBatchManager(this.connection);
    this.subcontractInvite = new SubcontractInviteManager(this.connection);
    this.aiCreditsTransaction = new AiCreditsTransactionManager(this.connection);
    this.aiModelRate = new AiModelRateManager(this.connection);
    this.adminNotification = new AdminNotificationManager(this.connection);
    this.payment = new PaymentManager(this.connection);
    this.paymentAllocation = new PaymentAllocationManager(this.connection);
    this.anonymizationEvent = new AnonymizationEventManager(this.connection);
    this.chaseRule = new ChaseRuleManager(this.connection);
    this.chaseLog = new ChaseLogManager(this.connection);
    this.accountingAuditLog = new AccountingAuditLogManager(this.connection);
    this.accountingConnection = new AccountingConnectionManager(this.connection);
    this.backfillJob = new BackfillJobManager(this.connection);
    this.reconcileCursor = new ReconcileCursorManager(this.connection);
    this.pendingCapture = new PendingCaptureManager(this.connection);
    this.user = new UserManager(this.connection);
    this.apprenticeConversation = new ApprenticeConversationManager(this.connection);
    this.apprenticeMessage = new ApprenticeMessageManager(this.connection);
    this.apprenticeResponse = new ApprenticeResponseManager(this.connection);
    this.stripeConnectAccount = new StripeConnectAccountManager(this.connection);
    this.stripeInvoice = new StripeInvoiceManager(this.connection);
    this.stripePaymentMethod = new StripePaymentMethodManager(this.connection);
    this.internal = {
      session: new InternalModelManager("session", this.connection, { "pluralApiIdentifier": "sessions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      client: new InternalModelManager("client", this.connection, { "pluralApiIdentifier": "clients", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      property: new InternalModelManager("property", this.connection, { "pluralApiIdentifier": "properties", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      job: new InternalModelManager("job", this.connection, { "pluralApiIdentifier": "jobs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      invoice: new InternalModelManager("invoice", this.connection, { "pluralApiIdentifier": "invoices", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      lineItem: new InternalModelManager("lineItem", this.connection, { "pluralApiIdentifier": "lineItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      profile: new InternalModelManager("profile", this.connection, { "pluralApiIdentifier": "profiles", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      pipelineStage: new InternalModelManager("pipelineStage", this.connection, { "pluralApiIdentifier": "pipelineStages", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      workItem: new InternalModelManager("workItem", this.connection, { "pluralApiIdentifier": "workItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      workItemEvent: new InternalModelManager("workItemEvent", this.connection, { "pluralApiIdentifier": "workItemEvents", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      workItemPhoto: new InternalModelManager("workItemPhoto", this.connection, { "pluralApiIdentifier": "workItemPhotos", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      quoteTemplate: new InternalModelManager("quoteTemplate", this.connection, { "pluralApiIdentifier": "quoteTemplates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      quoteTemplateItem: new InternalModelManager("quoteTemplateItem", this.connection, { "pluralApiIdentifier": "quoteTemplateItems", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      paymentMilestone: new InternalModelManager("paymentMilestone", this.connection, { "pluralApiIdentifier": "paymentMilestones", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      membership: new InternalModelManager("membership", this.connection, { "pluralApiIdentifier": "memberships", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      appliance: new InternalModelManager("appliance", this.connection, { "pluralApiIdentifier": "appliances", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      amendmentRequest: new InternalModelManager("amendmentRequest", this.connection, { "pluralApiIdentifier": "amendmentRequests", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      complianceCertificate: new InternalModelManager("complianceCertificate", this.connection, { "pluralApiIdentifier": "complianceCertificates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      propertyAccess: new InternalModelManager("propertyAccess", this.connection, { "pluralApiIdentifier": "propertyAccesses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      feedback: new InternalModelManager("feedback", this.connection, { "pluralApiIdentifier": "feedbacks", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripeSubscription: new InternalModelManager("stripeSubscription", this.connection, { "pluralApiIdentifier": "stripeSubscriptions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      clientTagAssignment: new InternalModelManager("clientTagAssignment", this.connection, { "pluralApiIdentifier": "clientTagAssignments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      clientTag: new InternalModelManager("clientTag", this.connection, { "pluralApiIdentifier": "clientTags", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      recurringEnrollment: new InternalModelManager("recurringEnrollment", this.connection, { "pluralApiIdentifier": "recurringEnrollments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      recurringServiceType: new InternalModelManager("recurringServiceType", this.connection, { "pluralApiIdentifier": "recurringServiceTypes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      propertyAccessScope: new InternalModelManager("propertyAccessScope", this.connection, { "pluralApiIdentifier": "propertyAccessScopes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      propertyIssue: new InternalModelManager("propertyIssue", this.connection, { "pluralApiIdentifier": "propertyIssues", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      profilePublicPhoto: new InternalModelManager("profilePublicPhoto", this.connection, { "pluralApiIdentifier": "profilePublicPhotos", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      profilePublicCategory: new InternalModelManager("profilePublicCategory", this.connection, { "pluralApiIdentifier": "profilePublicCategories", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      publicEnquiry: new InternalModelManager("publicEnquiry", this.connection, { "pluralApiIdentifier": "publicEnquiries", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      expense: new InternalModelManager("expense", this.connection, { "pluralApiIdentifier": "expenses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      auditEntry: new InternalModelManager("auditEntry", this.connection, { "pluralApiIdentifier": "auditEntries", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      reminder: new InternalModelManager("reminder", this.connection, { "pluralApiIdentifier": "reminders", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      quoteAttachment: new InternalModelManager("quoteAttachment", this.connection, { "pluralApiIdentifier": "quoteAttachments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      profilePublicService: new InternalModelManager("profilePublicService", this.connection, { "pluralApiIdentifier": "profilePublicServices", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      productService: new InternalModelManager("productService", this.connection, { "pluralApiIdentifier": "productServices", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      externalCalendarFeed: new InternalModelManager("externalCalendarFeed", this.connection, { "pluralApiIdentifier": "externalCalendarFeeds", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      creditNote: new InternalModelManager("creditNote", this.connection, { "pluralApiIdentifier": "creditNotes", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      tradeCategory: new InternalModelManager("tradeCategory", this.connection, { "pluralApiIdentifier": "tradeCategories", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      contactLinkCandidate: new InternalModelManager("contactLinkCandidate", this.connection, { "pluralApiIdentifier": "contactLinkCandidates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      unmatchedCategoryLog: new InternalModelManager("unmatchedCategoryLog", this.connection, { "pluralApiIdentifier": "unmatchedCategoryLogs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      referral: new InternalModelManager("referral", this.connection, { "pluralApiIdentifier": "referrals", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      membershipInvite: new InternalModelManager("membershipInvite", this.connection, { "pluralApiIdentifier": "membershipInvites", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      signedAcceptance: new InternalModelManager("signedAcceptance", this.connection, { "pluralApiIdentifier": "signedAcceptances", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripePayout: new InternalModelManager("stripePayout", this.connection, { "pluralApiIdentifier": "stripePayouts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripeCharge: new InternalModelManager("stripeCharge", this.connection, { "pluralApiIdentifier": "stripeCharges", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      manosFeeLedger: new InternalModelManager("manosFeeLedger", this.connection, { "pluralApiIdentifier": "manosFeeLedgers", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      importBatch: new InternalModelManager("importBatch", this.connection, { "pluralApiIdentifier": "importBatches", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      subcontractInvite: new InternalModelManager("subcontractInvite", this.connection, { "pluralApiIdentifier": "subcontractInvites", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      aiCreditsTransaction: new InternalModelManager("aiCreditsTransaction", this.connection, { "pluralApiIdentifier": "aiCreditsTransactions", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      aiModelRate: new InternalModelManager("aiModelRate", this.connection, { "pluralApiIdentifier": "aiModelRates", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      adminNotification: new InternalModelManager("adminNotification", this.connection, { "pluralApiIdentifier": "adminNotifications", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      payment: new InternalModelManager("payment", this.connection, { "pluralApiIdentifier": "payments", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      paymentAllocation: new InternalModelManager("paymentAllocation", this.connection, { "pluralApiIdentifier": "paymentAllocations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      anonymizationEvent: new InternalModelManager("anonymizationEvent", this.connection, { "pluralApiIdentifier": "anonymizationEvents", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      chaseRule: new InternalModelManager("chaseRule", this.connection, { "pluralApiIdentifier": "chaseRules", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      chaseLog: new InternalModelManager("chaseLog", this.connection, { "pluralApiIdentifier": "chaseLogs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      accountingAuditLog: new InternalModelManager("accountingAuditLog", this.connection, { "pluralApiIdentifier": "accountingAuditLogs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      accountingConnection: new InternalModelManager("accountingConnection", this.connection, { "pluralApiIdentifier": "accountingConnections", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      backfillJob: new InternalModelManager("backfillJob", this.connection, { "pluralApiIdentifier": "backfillJobs", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      reconcileCursor: new InternalModelManager("reconcileCursor", this.connection, { "pluralApiIdentifier": "reconcileCursors", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      pendingCapture: new InternalModelManager("pendingCapture", this.connection, { "pluralApiIdentifier": "pendingCaptures", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      user: new InternalModelManager("user", this.connection, { "pluralApiIdentifier": "users", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      apprenticeConversation: new InternalModelManager("apprenticeConversation", this.connection, { "pluralApiIdentifier": "apprenticeConversations", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      apprenticeMessage: new InternalModelManager("apprenticeMessage", this.connection, { "pluralApiIdentifier": "apprenticeMessages", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      apprenticeResponse: new InternalModelManager("apprenticeResponse", this.connection, { "pluralApiIdentifier": "apprenticeResponses", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripeConnectAccount: new InternalModelManager("stripeConnectAccount", this.connection, { "pluralApiIdentifier": "stripeConnectAccounts", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripeInvoice: new InternalModelManager("stripeInvoice", this.connection, { "pluralApiIdentifier": "stripeInvoices", "hasAmbiguousIdentifiers": false, "namespace": [] }),
      stripePaymentMethod: new InternalModelManager("stripePaymentMethod", this.connection, { "pluralApiIdentifier": "stripePaymentMethods", "hasAmbiguousIdentifiers": false, "namespace": [] })
    };
  }
  /**
   * Returns a new Client instance that will call the Gadget API as the application's admin user.
   * This can only be used for API clients using internal authentication.
   * @returns {ManosClient} A new ManosClient instance with admin authentication
   */
  get actAsAdmin() {
    assert(this.options?.authenticationMode?.internal, `actAsAdmin can only be used for API clients using internal authentication, this client is using ${JSON.stringify(this.options?.authenticationMode)}`);
    return new ManosClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: false
        }
      }
    });
  }
  /**
   * Returns a new ManosClient instance that will call the Gadget API as with the permission of the current session.
   * This can only be used for API clients using internal authentication.
   * @returns {ManosClient} A new ManosClient instance with session authentication
   */
  get actAsSession() {
    assert(this.options?.authenticationMode?.internal, "actAsSession can only be used for API clients using internal authentication");
    return new ManosClient({
      ...this.options,
      authenticationMode: {
        internal: {
          ...this.options.authenticationMode.internal,
          actAsSession: true
        }
      }
    });
  }
  /** Run an arbitrary GraphQL query. */
  async query(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.query(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /** Run an arbitrary GraphQL mutation. */
  async mutate(graphQL, variables, options) {
    const { data, error } = await this.connection.currentClient.mutation(graphQL, variables, options).toPromise();
    if (error)
      throw error;
    return data;
  }
  /**
   * `fetch` function that works the same as the built-in `fetch` function, but automatically passes authentication information for this API client.
   *
   * @example
   * await api.fetch("https://myapp--development.gadget.app/foo/bar");
   *
   * @example
   * // fetch a relative URL from the endpoint this API client is configured to fetch from
   * await api.fetch("/foo/bar");
   **/
  async fetch(input, init = {}) {
    return await this.connection.fetch(input, init);
  }
  async enqueue(action, inputOrOptions, maybeOptions) {
    assert(action, ".enqueue must be passed an action as the first argument but was passed undefined");
    let input;
    let options;
    if (typeof maybeOptions !== "undefined") {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = maybeOptions;
    } else if (!action.variables || Object.keys(action.variables).length == 0) {
      input = {};
      options = inputOrOptions;
    } else {
      if (typeof inputOrOptions == "string") {
        input = { id: inputOrOptions };
      } else {
        input = inputOrOptions;
      }
      options = {};
    }
    return await enqueueActionRunner(this.connection, action, input, options);
  }
  /**
   * Returns a handle for a given background action id
   *
   * @param action The action that was enqueued
   * @param id The id of the background action
   *
   * @example
   * const handle = api.handle(api.widget.update, "app-job-12346");
   *
   * @example
   * const handle = api.handle(api.someGlobalAction, "app-job-56789");
   **/
  handle(action, id) {
    return new BackgroundActionHandle(this.connection, action, id);
  }
  toString() {
    return `ManosClient<${this.environment}>`;
  }
  toJSON() {
    return this.toString();
  }
}
ManosClient.prototype[$modelRelationships] = { "session": { "user": { "type": "BelongsTo", "model": "user" } }, "client": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" }, "createdByUser": { "type": "BelongsTo", "model": "user" }, "profileLink": { "type": "BelongsTo", "model": "profile" } }, "property": { "profile": { "type": "BelongsTo", "model": "profile" }, "client": { "type": "BelongsTo", "model": "client" } }, "job": { "profile": { "type": "BelongsTo", "model": "profile" }, "client": { "type": "BelongsTo", "model": "client" }, "property": { "type": "BelongsTo", "model": "property" } }, "invoice": { "profile": { "type": "BelongsTo", "model": "profile" }, "client": { "type": "BelongsTo", "model": "client" }, "job": { "type": "BelongsTo", "model": "job" }, "workItem": { "type": "BelongsTo", "model": "workItem" }, "creditNotes": { "type": "HasMany", "model": "creditNote" }, "lineItems": { "type": "HasMany", "model": "lineItem" }, "allocations": { "type": "HasMany", "model": "paymentAllocation" } }, "lineItem": { "invoice": { "type": "BelongsTo", "model": "invoice" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "profile": { "user": { "type": "BelongsTo", "model": "user" }, "stripeSubscription": { "type": "HasOne", "model": "stripeSubscription" }, "workItems": { "type": "HasMany", "model": "workItem" }, "aiCreditsTransactions": { "type": "HasMany", "model": "aiCreditsTransaction" } }, "pipelineStage": { "profile": { "type": "BelongsTo", "model": "profile" } }, "workItem": { "currentStage": { "type": "BelongsTo", "model": "pipelineStage" }, "client": { "type": "BelongsTo", "model": "client" }, "property": { "type": "BelongsTo", "model": "property" }, "profile": { "type": "BelongsTo", "model": "profile" }, "invoice": { "type": "BelongsTo", "model": "invoice" }, "parentWorkItem": { "type": "BelongsTo", "model": "workItem" }, "assignedToMembership": { "type": "BelongsTo", "model": "membership" }, "subcontractedFromWorkItem": { "type": "BelongsTo", "model": "workItem" } }, "workItemEvent": { "workItem": { "type": "BelongsTo", "model": "workItem" }, "profile": { "type": "BelongsTo", "model": "profile" }, "actor": { "type": "BelongsTo", "model": "user" } }, "workItemPhoto": { "workItem": { "type": "BelongsTo", "model": "workItem" }, "profile": { "type": "BelongsTo", "model": "profile" }, "uploadedBy": { "type": "BelongsTo", "model": "user" } }, "quoteTemplate": { "profile": { "type": "BelongsTo", "model": "profile" } }, "quoteTemplateItem": { "template": { "type": "BelongsTo", "model": "quoteTemplate" } }, "paymentMilestone": { "workItem": { "type": "BelongsTo", "model": "workItem" }, "invoice": { "type": "BelongsTo", "model": "invoice" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "membership": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" } }, "appliance": { "property": { "type": "BelongsTo", "model": "property" } }, "amendmentRequest": { "requestedBy": { "type": "BelongsTo", "model": "user" } }, "complianceCertificate": { "property": { "type": "BelongsTo", "model": "property" } }, "propertyAccess": { "user": { "type": "BelongsTo", "model": "user" }, "property": { "type": "BelongsTo", "model": "property" }, "parentAccess": { "type": "BelongsTo", "model": "propertyAccess" }, "scopes": { "type": "HasMany", "model": "propertyAccessScope" } }, "feedback": { "user": { "type": "BelongsTo", "model": "user" } }, "stripeSubscription": { "profile": { "type": "BelongsTo", "model": "profile" } }, "clientTagAssignment": { "clientTag": { "type": "BelongsTo", "model": "clientTag" }, "client": { "type": "BelongsTo", "model": "client" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "clientTag": { "profile": { "type": "BelongsTo", "model": "profile" } }, "recurringEnrollment": { "client": { "type": "BelongsTo", "model": "client" }, "profile": { "type": "BelongsTo", "model": "profile" }, "recurringServiceType": { "type": "BelongsTo", "model": "recurringServiceType" }, "sourceWorkItem": { "type": "BelongsTo", "model": "workItem" } }, "recurringServiceType": { "profile": { "type": "BelongsTo", "model": "profile" } }, "propertyAccessScope": { "delegationParentAccess": { "type": "BelongsTo", "model": "propertyAccess" }, "grantedBy": { "type": "BelongsTo", "model": "user" }, "parentGrant": { "type": "BelongsTo", "model": "propertyAccessScope" }, "propertyAccess": { "type": "BelongsTo", "model": "propertyAccess" }, "revokedBy": { "type": "BelongsTo", "model": "user" }, "requestedBy": { "type": "BelongsTo", "model": "user" } }, "propertyIssue": { "acknowledgedBy": { "type": "BelongsTo", "model": "user" }, "escalatedTo": { "type": "BelongsTo", "model": "user" }, "property": { "type": "BelongsTo", "model": "property" }, "relatedWorkItem": { "type": "BelongsTo", "model": "workItem" }, "reportedBy": { "type": "BelongsTo", "model": "user" }, "resolvedBy": { "type": "BelongsTo", "model": "user" } }, "profilePublicPhoto": { "profile": { "type": "BelongsTo", "model": "profile" } }, "profilePublicCategory": { "profile": { "type": "BelongsTo", "model": "profile" }, "tradeCategory": { "type": "BelongsTo", "model": "tradeCategory" } }, "publicEnquiry": { "profile": { "type": "BelongsTo", "model": "profile" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "expense": { "profile": { "type": "BelongsTo", "model": "profile" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "auditEntry": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" } }, "reminder": { "invoice": { "type": "BelongsTo", "model": "invoice" }, "profile": { "type": "BelongsTo", "model": "profile" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "quoteAttachment": { "profile": { "type": "BelongsTo", "model": "profile" }, "uploadedByUser": { "type": "BelongsTo", "model": "user" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "profilePublicService": { "profile": { "type": "BelongsTo", "model": "profile" } }, "productService": { "profile": { "type": "BelongsTo", "model": "profile" } }, "externalCalendarFeed": { "membership": { "type": "BelongsTo", "model": "membership" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "creditNote": { "originalInvoice": { "type": "BelongsTo", "model": "invoice" }, "profile": { "type": "BelongsTo", "model": "profile" }, "sentToClientBy": { "type": "BelongsTo", "model": "user" } }, "tradeCategory": { "parent": { "type": "BelongsTo", "model": "tradeCategory" } }, "contactLinkCandidate": { "candidateProfile": { "type": "BelongsTo", "model": "profile" }, "candidateUser": { "type": "BelongsTo", "model": "user" }, "client": { "type": "BelongsTo", "model": "client" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "unmatchedCategoryLog": {}, "referral": { "referredUser": { "type": "BelongsTo", "model": "user" }, "referrerProfile": { "type": "BelongsTo", "model": "profile" } }, "membershipInvite": { "acceptedUser": { "type": "BelongsTo", "model": "user" }, "invitedBy": { "type": "BelongsTo", "model": "user" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "signedAcceptance": { "invoice": { "type": "BelongsTo", "model": "invoice" }, "signerEntityProfile": { "type": "BelongsTo", "model": "profile" }, "signerUser": { "type": "BelongsTo", "model": "user" }, "supersedesAcceptance": { "type": "BelongsTo", "model": "signedAcceptance" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "stripePayout": { "profile": { "type": "BelongsTo", "model": "profile" } }, "stripeCharge": { "invoice": { "type": "BelongsTo", "model": "invoice" }, "payout": { "type": "BelongsTo", "model": "stripePayout" }, "profile": { "type": "BelongsTo", "model": "profile" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "manosFeeLedger": { "profile": { "type": "BelongsTo", "model": "profile" } }, "importBatch": { "profile": { "type": "BelongsTo", "model": "profile" } }, "subcontractInvite": { "contractorWorkItem": { "type": "BelongsTo", "model": "workItem" }, "parentWorkItem": { "type": "BelongsTo", "model": "workItem" }, "targetProfile": { "type": "BelongsTo", "model": "profile" } }, "aiCreditsTransaction": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" } }, "aiModelRate": {}, "adminNotification": {}, "payment": { "allocations": { "type": "HasMany", "model": "paymentAllocation" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "paymentAllocation": { "invoice": { "type": "BelongsTo", "model": "invoice" }, "payment": { "type": "BelongsTo", "model": "payment" }, "paymentMilestone": { "type": "BelongsTo", "model": "paymentMilestone" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "anonymizationEvent": { "profile": { "type": "BelongsTo", "model": "profile" }, "triggeredBy": { "type": "BelongsTo", "model": "user" } }, "chaseRule": { "chaseLogs": { "type": "HasMany", "model": "chaseLog" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "chaseLog": { "chaseRule": { "type": "BelongsTo", "model": "chaseRule" }, "profile": { "type": "BelongsTo", "model": "profile" }, "workItem": { "type": "BelongsTo", "model": "workItem" } }, "accountingAuditLog": { "connection": { "type": "BelongsTo", "model": "accountingConnection" } }, "accountingConnection": { "profile": { "type": "BelongsTo", "model": "profile" } }, "backfillJob": { "accountingConnection": { "type": "BelongsTo", "model": "accountingConnection" } }, "reconcileCursor": { "accountingConnection": { "type": "BelongsTo", "model": "accountingConnection" } }, "pendingCapture": { "profile": { "type": "BelongsTo", "model": "profile" } }, "user": { "profile": { "type": "HasOne", "model": "profile" } }, "apprenticeConversation": { "profile": { "type": "BelongsTo", "model": "profile" } }, "apprenticeMessage": { "conversation": { "type": "BelongsTo", "model": "apprenticeConversation" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "apprenticeResponse": { "conversation": { "type": "BelongsTo", "model": "apprenticeConversation" }, "profile": { "type": "BelongsTo", "model": "profile" } }, "stripeConnectAccount": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" } }, "stripeInvoice": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" } }, "stripePaymentMethod": { "profile": { "type": "BelongsTo", "model": "profile" }, "user": { "type": "BelongsTo", "model": "user" } } };
const coreImplementation = {
  GadgetRecord,
  disambiguateActionVariables,
  disambiguateBulkActionVariables,
  capitalizeIdentifier,
  wrapClientSideError: ErrorWrapper.forClientSideError,
  errorIfDataAbsent: ErrorWrapper.errorIfDataAbsent,
  namespaceDataPath,
  camelize,
  isEqual
};
ManosClient.prototype[$coreImplementation] = coreImplementation;
const Client = ManosClient;
export {
  Client,
  DefaultAccountingAuditLogSelection2 as DefaultAccountingAuditLogSelection,
  DefaultAccountingConnectionSelection2 as DefaultAccountingConnectionSelection,
  DefaultAdminNotificationSelection2 as DefaultAdminNotificationSelection,
  DefaultAiCreditsTransactionSelection2 as DefaultAiCreditsTransactionSelection,
  DefaultAiModelRateSelection2 as DefaultAiModelRateSelection,
  DefaultAmendmentRequestSelection2 as DefaultAmendmentRequestSelection,
  DefaultAnonymizationEventSelection2 as DefaultAnonymizationEventSelection,
  DefaultApplianceSelection2 as DefaultApplianceSelection,
  DefaultApprenticeConversationSelection2 as DefaultApprenticeConversationSelection,
  DefaultApprenticeMessageSelection2 as DefaultApprenticeMessageSelection,
  DefaultApprenticeResponseSelection2 as DefaultApprenticeResponseSelection,
  DefaultAuditEntrySelection2 as DefaultAuditEntrySelection,
  DefaultBackfillJobSelection2 as DefaultBackfillJobSelection,
  DefaultChaseLogSelection2 as DefaultChaseLogSelection,
  DefaultChaseRuleSelection2 as DefaultChaseRuleSelection,
  DefaultClientSelection2 as DefaultClientSelection,
  DefaultClientTagAssignmentSelection2 as DefaultClientTagAssignmentSelection,
  DefaultClientTagSelection2 as DefaultClientTagSelection,
  DefaultComplianceCertificateSelection2 as DefaultComplianceCertificateSelection,
  DefaultContactLinkCandidateSelection2 as DefaultContactLinkCandidateSelection,
  DefaultCreditNoteSelection2 as DefaultCreditNoteSelection,
  DefaultExpenseSelection2 as DefaultExpenseSelection,
  DefaultExternalCalendarFeedSelection2 as DefaultExternalCalendarFeedSelection,
  DefaultFeedbackSelection2 as DefaultFeedbackSelection,
  DefaultImportBatchSelection2 as DefaultImportBatchSelection,
  DefaultInvoiceSelection2 as DefaultInvoiceSelection,
  DefaultJobSelection2 as DefaultJobSelection,
  DefaultLineItemSelection2 as DefaultLineItemSelection,
  DefaultManosFeeLedgerSelection2 as DefaultManosFeeLedgerSelection,
  DefaultMembershipInviteSelection2 as DefaultMembershipInviteSelection,
  DefaultMembershipSelection2 as DefaultMembershipSelection,
  DefaultPaymentAllocationSelection2 as DefaultPaymentAllocationSelection,
  DefaultPaymentMilestoneSelection2 as DefaultPaymentMilestoneSelection,
  DefaultPaymentSelection2 as DefaultPaymentSelection,
  DefaultPendingCaptureSelection2 as DefaultPendingCaptureSelection,
  DefaultPipelineStageSelection2 as DefaultPipelineStageSelection,
  DefaultProductServiceSelection2 as DefaultProductServiceSelection,
  DefaultProfilePublicCategorySelection2 as DefaultProfilePublicCategorySelection,
  DefaultProfilePublicPhotoSelection2 as DefaultProfilePublicPhotoSelection,
  DefaultProfilePublicServiceSelection2 as DefaultProfilePublicServiceSelection,
  DefaultProfileSelection2 as DefaultProfileSelection,
  DefaultPropertyAccessScopeSelection2 as DefaultPropertyAccessScopeSelection,
  DefaultPropertyAccessSelection2 as DefaultPropertyAccessSelection,
  DefaultPropertyIssueSelection2 as DefaultPropertyIssueSelection,
  DefaultPropertySelection2 as DefaultPropertySelection,
  DefaultPublicEnquirySelection2 as DefaultPublicEnquirySelection,
  DefaultQuoteAttachmentSelection2 as DefaultQuoteAttachmentSelection,
  DefaultQuoteTemplateItemSelection2 as DefaultQuoteTemplateItemSelection,
  DefaultQuoteTemplateSelection2 as DefaultQuoteTemplateSelection,
  DefaultReconcileCursorSelection2 as DefaultReconcileCursorSelection,
  DefaultRecurringEnrollmentSelection2 as DefaultRecurringEnrollmentSelection,
  DefaultRecurringServiceTypeSelection2 as DefaultRecurringServiceTypeSelection,
  DefaultReferralSelection2 as DefaultReferralSelection,
  DefaultReminderSelection2 as DefaultReminderSelection,
  DefaultSessionSelection2 as DefaultSessionSelection,
  DefaultSignedAcceptanceSelection2 as DefaultSignedAcceptanceSelection,
  DefaultStripeChargeSelection2 as DefaultStripeChargeSelection,
  DefaultStripeConnectAccountSelection2 as DefaultStripeConnectAccountSelection,
  DefaultStripeInvoiceSelection2 as DefaultStripeInvoiceSelection,
  DefaultStripePaymentMethodSelection2 as DefaultStripePaymentMethodSelection,
  DefaultStripePayoutSelection2 as DefaultStripePayoutSelection,
  DefaultStripeSubscriptionSelection2 as DefaultStripeSubscriptionSelection,
  DefaultSubcontractInviteSelection2 as DefaultSubcontractInviteSelection,
  DefaultTradeCategorySelection2 as DefaultTradeCategorySelection,
  DefaultUnmatchedCategoryLogSelection2 as DefaultUnmatchedCategoryLogSelection,
  DefaultUserSelection2 as DefaultUserSelection,
  DefaultWorkItemEventSelection2 as DefaultWorkItemEventSelection,
  DefaultWorkItemPhotoSelection2 as DefaultWorkItemPhotoSelection,
  DefaultWorkItemSelection2 as DefaultWorkItemSelection,
  ManosClient,
  maybeGetAuthenticationModeOptionsFromClientOptions
};
//# sourceMappingURL=Client.js.map
